import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Mail, CheckCircle, XCircle, Power, PowerOff, ExternalLink, Loader2,
  Copy, Eye, EyeOff, Upload, RefreshCw, Send, Trash2, KeyRound, Link as LinkIcon,
  ShieldCheck, AlertTriangle, FileJson,
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = (typeof window !== 'undefined' && window.location && window.location.origin)
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL;

const Section = ({ icon: Icon, title, subtitle, right, children, testid }) => (
  <div className="bg-white border border-gray-200 rounded-xl" data-testid={testid}>
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
      <div className="flex items-center gap-2.5">
        <span className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center"><Icon size={16} className="text-gray-600" /></span>
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-[11px] text-gray-500">{subtitle}</p>}
        </div>
      </div>
      {right}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const CopyField = ({ value, testid }) => {
  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(value || ''); toast.success('Copied'); } catch { toast.error('Copy failed'); }
  };
  return (
    <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
      <code className="text-[11px] font-mono text-gray-800 flex-1 break-all" data-testid={testid}>{value || '—'}</code>
      <button onClick={handleCopy} className="shrink-0 p-1 hover:bg-gray-200 rounded" title="Copy">
        <Copy size={13} className="text-gray-600" />
      </button>
    </div>
  );
};

const DebugRow = ({ label, value, ok, bad }) => {
  const tone = bad ? 'text-red-700' : ok ? 'text-emerald-700' : 'text-gray-700';
  return (
    <div className="flex items-center justify-between bg-gray-50 rounded px-2 py-1">
      <span className="text-gray-500 uppercase tracking-wide">{label}</span>
      <span className={`font-medium ${tone}`}>{value}</span>
    </div>
  );
};

const GmailIntegrationSettings = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const token = useMemo(() => localStorage.getItem('token'), []);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  // Status / Connection
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Credentials
  const [creds, setCreds] = useState(null);
  const [credsLoading, setCredsLoading] = useState(true);
  const [showSecret, setShowSecret] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState('');
  const [editClientId, setEditClientId] = useState('');
  const [editClientSecret, setEditClientSecret] = useState('');
  const [editProjectId, setEditProjectId] = useState('');
  const [savingCreds, setSavingCreds] = useState(false);

  // Manual OAuth flow
  const [authData, setAuthData] = useState(null);
  const [authCode, setAuthCode] = useState('');
  const [connecting, setConnecting] = useState(false);

  // Tools
  const [validating, setValidating] = useState(false);
  const [testingConn, setTestingConn] = useState(false);
  const [connTest, setConnTest] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/gmail/status`, { headers });
      if (r.ok) setStatus(await r.json());
    } catch { /* */ } finally { setLoading(false); }
  }, [headers]);

  const fetchCreds = useCallback(async () => {
    try {
      setCredsLoading(true);
      const r = await fetch(`${API_BASE}/api/gmail/credentials`, { headers });
      if (r.ok) {
        const d = await r.json();
        setCreds(d);
        setEditClientId(d.client_id || '');
        setEditProjectId(d.project_id || '');
        setEditClientSecret('');
        setRevealedSecret('');
        setShowSecret(false);
      }
    } catch { /* */ } finally { setCredsLoading(false); }
  }, [headers]);

  useEffect(() => { fetchStatus(); fetchCreds(); }, [fetchStatus, fetchCreds]);

  // Handle ?gmail_connected / ?gmail_error redirect params (legacy server callback)
  useEffect(() => {
    if (searchParams.get('gmail_connected')) { toast.success('Gmail connected'); setSearchParams({}, { replace: true }); fetchStatus(); }
    const err = searchParams.get('gmail_error');
    if (err) { toast.error(`Gmail error: ${err}`, { duration: 6000 }); setSearchParams({}, { replace: true }); }
  }, [searchParams, setSearchParams, fetchStatus]);

  // -------------------- Credentials Manager --------------------
  const handleRevealSecret = async () => {
    if (showSecret && revealedSecret) { setShowSecret(false); return; }
    try {
      const r = await fetch(`${API_BASE}/api/gmail/credentials/reveal`, { method: 'POST', headers });
      if (r.ok) { const d = await r.json(); setRevealedSecret(d.client_secret || ''); setShowSecret(true); }
      else toast.error('Reveal failed');
    } catch { toast.error('Reveal failed'); }
  };

  const handleUploadJson = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const r = await fetch(`${API_BASE}/api/gmail/credentials`, {
        method: 'PUT', headers, body: JSON.stringify({ json_blob: json }),
      });
      if (r.ok) { toast.success('Credentials saved from JSON'); fetchCreds(); }
      else { const e = await r.json(); toast.error(e.detail || 'Save failed'); }
    } catch (e) { toast.error('Could not parse JSON file'); }
  };

  const handleSaveCreds = async () => {
    if (!editClientId.trim() || !editClientSecret.trim()) {
      toast.error('Client ID and Client Secret are required');
      return;
    }
    try {
      setSavingCreds(true);
      const r = await fetch(`${API_BASE}/api/gmail/credentials`, {
        method: 'PUT', headers,
        body: JSON.stringify({ client_id: editClientId.trim(), client_secret: editClientSecret.trim(), project_id: editProjectId.trim() }),
      });
      if (r.ok) { toast.success('Credentials saved'); fetchCreds(); fetchStatus(); }
      else { const e = await r.json(); toast.error(e.detail || 'Save failed'); }
    } catch { toast.error('Save failed'); }
    finally { setSavingCreds(false); }
  };

  const handleDeleteCreds = async () => {
    if (!window.confirm('Remove the saved credentials? Falls back to environment variables.')) return;
    try {
      const r = await fetch(`${API_BASE}/api/gmail/credentials`, { method: 'DELETE', headers });
      if (r.ok) { toast.success('Credentials removed'); fetchCreds(); fetchStatus(); }
      else toast.error('Delete failed');
    } catch { toast.error('Delete failed'); }
  };

  // -------------------- Manual OAuth --------------------
  const handleStartAuth = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/gmail/authorize`, { method: 'POST', headers });
      if (r.ok) { setAuthData(await r.json()); setAuthCode(''); toast.success('Auth URL generated'); }
      else { const e = await r.json(); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Failed'); }
  };
  const handleSubmitCode = async () => {
    if (!authCode.trim()) return toast.error('Paste the returned code or URL');
    setConnecting(true);
    try {
      const r = await fetch(`${API_BASE}/api/gmail/callback`, {
        method: 'POST', headers, body: JSON.stringify({ code: authCode.trim() }),
      });
      if (r.ok) { toast.success('Gmail connected'); setAuthData(null); setAuthCode(''); fetchStatus(); }
      else { const e = await r.json(); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Connection failed'); }
    finally { setConnecting(false); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Gmail? Emails will fall back to simulation mode.')) return;
    try {
      const r = await fetch(`${API_BASE}/api/gmail/disconnect`, { method: 'POST', headers });
      if (r.ok) { toast.success('Disconnected'); fetchStatus(); } else toast.error('Failed');
    } catch { toast.error('Failed'); }
  };

  const handleToggle = async () => {
    const next = !(status?.is_active);
    try {
      const r = await fetch(`${API_BASE}/api/gmail/toggle`, {
        method: 'POST', headers, body: JSON.stringify({ is_active: next }),
      });
      if (r.ok) {
        const data = await r.json();
        if (data.gmail_status) setStatus(data.gmail_status);
        else fetchStatus();
        toast.success(next ? 'Gmail activated successfully' : 'Gmail disabled successfully');
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Toggle failed');
      }
    } catch { toast.error('Toggle failed'); }
  };

  // -------------------- Tools --------------------
  const handleValidate = async () => {
    setValidating(true);
    try {
      const r = await fetch(`${API_BASE}/api/gmail/config-check`, { headers });
      const d = await r.json();
      if (d.valid) toast.success('Configuration looks valid');
      else toast.error(`Issues: ${(d.issues || []).join(', ')}`, { duration: 7000 });
    } catch { toast.error('Validation failed'); }
    finally { setValidating(false); }
  };

  const handleTestConnection = async () => {
    setTestingConn(true);
    try {
      const r = await fetch(`${API_BASE}/api/gmail/test-connection`, { method: 'POST', headers });
      const d = await r.json();
      setConnTest(d);
      if (d.ok) toast.success(`Connection OK · stage: ${d.stage}`);
      else toast.error(`Failed at ${d.stage}: ${d.detail || (d.issues || []).join(', ')}`);
    } catch { toast.error('Test failed'); }
    finally { setTestingConn(false); }
  };

  const handleSendTest = async () => {
    if (!testEmail.trim()) return toast.error('Enter recipient email');
    setSending(true);
    try {
      const r = await fetch(`${API_BASE}/api/gmail/send-test`, {
        method: 'POST', headers, body: JSON.stringify({ to: testEmail.trim() }),
      });
      const d = await r.json();
      if (d.success || d.status === 'success') toast.success('Test email sent');
      else toast.error(d.error || d.message || 'Send failed');
    } catch { toast.error('Send failed'); }
    finally { setSending(false); }
  };

  const connected = status?.connected && status?.is_active !== false;
  const secretDisplay = showSecret && revealedSecret ? revealedSecret : (creds?.client_secret_masked || '');
  const sourceBadge = creds?.source === 'db'
    ? <span className="px-2 py-0.5 text-[10px] bg-emerald-100 text-emerald-700 rounded">DB</span>
    : <span className="px-2 py-0.5 text-[10px] bg-gray-100 text-gray-600 rounded">ENV</span>;

  return (
    <div className="max-w-5xl mx-auto p-2 space-y-4" data-testid="gmail-integration-settings-v2">
      {/* Title */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center"><Mail size={20} className="text-red-600" /></span>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Gmail Integration</h1>
            <p className="text-xs text-gray-500">Send emails from your Gmail account via OAuth.</p>
          </div>
        </div>
        <button onClick={() => { fetchStatus(); fetchCreds(); }} className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* CARD 1 — Status */}
      <Section icon={connected ? CheckCircle : XCircle} title="Connection status" testid="gmail-card-status">
        {loading ? (
          <div className="text-sm text-gray-500"><Loader2 size={14} className="inline animate-spin mr-1" />Loading…</div>
        ) : (
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                {connected ? (
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded">Connected</span>
                ) : status?.connected && !status?.is_active ? (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded">Connected (Disabled)</span>
                ) : (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-semibold rounded">Not connected</span>
                )}
                {status?.environment && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] uppercase rounded">{status.environment}</span>
                )}
              </div>
              {status?.connected_email && (
                <p className="text-sm text-gray-800"><strong>{status.connected_email}</strong></p>
              )}
              {status?.connected_at && (
                <p className="text-xs text-gray-500 mt-0.5">Connected {new Date(status.connected_at).toLocaleString()}</p>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              {connected ? (
                <>
                  <button onClick={handleToggle} className="text-xs flex items-center gap-1 text-amber-700 hover:underline" data-testid="gmail-toggle-btn">
                    <PowerOff size={12} /> Disable
                  </button>
                  <button onClick={handleStartAuth} className="text-xs flex items-center gap-1 text-blue-700 hover:underline">
                    <RefreshCw size={12} /> Reconnect
                  </button>
                  <button onClick={handleDisconnect} className="text-xs flex items-center gap-1 text-red-700 hover:underline" data-testid="gmail-disconnect-btn">
                    <Trash2 size={12} /> Disconnect
                  </button>
                </>
              ) : status?.connected && !status?.is_active ? (
                <button onClick={handleToggle} className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded hover:bg-emerald-700 flex items-center gap-1" data-testid="gmail-enable-btn">
                  <Power size={12} /> Enable
                </button>
              ) : null}
            </div>
          </div>
        )}

        {status && (
          <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px]" data-testid="gmail-debug-panel">
            <DebugRow label="Provider" value="Gmail" />
            <DebugRow label="Connected" value={status.connected ? 'Yes' : 'No'} ok={status.connected} />
            <DebugRow label="Active" value={status.is_active ? 'Yes' : 'No'} ok={status.is_active} bad={status.connected && !status.is_active} />
            <DebugRow label="Token present" value={status.has_refresh_token ? 'Yes' : 'No'} ok={status.has_refresh_token} />
            <DebugRow label="Last updated" value={status.updated_at ? new Date(status.updated_at).toLocaleString() : '—'} />
            <DebugRow label="Mode" value={status.environment || '—'} />
          </div>
        )}
      </Section>

      {/* CARD 2 — Credentials Manager */}
      <Section
        icon={KeyRound}
        title="Credentials"
        subtitle={creds?.updated_at ? `Last updated ${new Date(creds.updated_at).toLocaleString()} by ${creds.updated_by_name || '—'}` : 'OAuth Client ID, Secret & Project ID'}
        right={creds && sourceBadge}
        testid="gmail-card-credentials"
      >
        {credsLoading ? (
          <Loader2 size={14} className="animate-spin text-gray-400" />
        ) : (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-xs text-teal-700 cursor-pointer">
              <input type="file" accept=".json,application/json" className="hidden" onChange={(e) => handleUploadJson(e.target.files?.[0])} data-testid="gmail-creds-upload-input" />
              <span className="px-3 py-1.5 border border-dashed border-teal-400 rounded-lg flex items-center gap-1.5 hover:bg-teal-50">
                <Upload size={12} /> Upload Google OAuth JSON
              </span>
              <span className="text-gray-400 text-[11px]">— auto-fills the fields below</span>
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">Client ID</label>
                <input
                  type="text" value={editClientId} onChange={(e) => setEditClientId(e.target.value)}
                  placeholder="123…apps.googleusercontent.com"
                  className="w-full px-3 py-2 text-xs font-mono border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="gmail-creds-client-id"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">Project ID <span className="text-gray-400 normal-case">(optional)</span></label>
                <input
                  type="text" value={editProjectId} onChange={(e) => setEditProjectId(e.target.value)}
                  placeholder="my-project-123"
                  className="w-full px-3 py-2 text-xs font-mono border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="gmail-creds-project-id"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">Client Secret</label>
                <div className="flex gap-2">
                  <input
                    type={showSecret ? 'text' : 'password'}
                    value={editClientSecret || (showSecret ? revealedSecret : '') || (editClientSecret === '' ? '' : '')}
                    onChange={(e) => setEditClientSecret(e.target.value)}
                    placeholder={creds?.client_secret_masked ? `Current: ${creds.client_secret_masked}` : 'GOCSPX-…'}
                    className="flex-1 px-3 py-2 text-xs font-mono border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                    data-testid="gmail-creds-client-secret"
                  />
                  <button
                    type="button"
                    onClick={handleRevealSecret}
                    className="px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                    title={showSecret ? 'Hide' : 'Reveal saved secret'}
                    data-testid="gmail-creds-reveal-btn"
                  >
                    {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {showSecret && revealedSecret && (
                  <div className="mt-1 text-[11px] text-gray-500 font-mono break-all">Saved: <strong>{secretDisplay}</strong></div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <button onClick={handleSaveCreds} disabled={savingCreds} className="px-4 py-1.5 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-1.5" data-testid="gmail-creds-save-btn">
                {savingCreds ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
                {creds?.source === 'db' ? 'Replace Credentials' : 'Save Credentials'}
              </button>
              {creds?.source === 'db' && (
                <button onClick={handleDeleteCreds} className="px-3 py-1.5 border border-red-200 text-red-700 text-xs rounded-lg hover:bg-red-50 flex items-center gap-1.5" data-testid="gmail-creds-delete-btn">
                  <Trash2 size={12} /> Delete
                </button>
              )}
              <span className="text-[11px] text-gray-400">Stored encrypted at rest. Falls back to environment variables when no DB record exists.</span>
            </div>
          </div>
        )}
      </Section>

      {/* CARD 3 — OAuth URLs */}
      <Section icon={LinkIcon} title="OAuth URLs" subtitle="Add these to your Google Cloud OAuth Client" testid="gmail-card-urls">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Authorized Redirect URI</p>
            <CopyField value={status?.redirect_uri} testid="gmail-redirect-uri-value" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Authorized Origin</p>
            <CopyField value={status?.diagnostics?.resolvedBaseUrl || (typeof window !== 'undefined' ? window.location.origin : '')} testid="gmail-origin-value" />
          </div>
        </div>
      </Section>

      {/* CARD 4 — Manual Connect Flow */}
      {!connected && (
        <Section icon={Mail} title="Connect Gmail" subtitle="Three quick steps" testid="gmail-card-connect">
          <ol className="space-y-3 text-sm">
            <li>
              <button onClick={handleStartAuth} className="px-4 py-2 bg-red-500 text-white text-xs font-semibold rounded-lg hover:bg-red-600 flex items-center gap-1.5" data-testid="gmail-connect-btn">
                <Mail size={14} /> {authData ? '1. Re-generate Auth URL' : '1. Generate Auth URL'}
              </button>
            </li>
            {authData?.auth_url && (
              <>
                <li>
                  <p className="text-xs text-gray-600 mb-1">2. Open this URL → sign in to Google → approve scopes</p>
                  <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                    <code className="text-[11px] font-mono text-gray-800 flex-1 break-all" data-testid="gmail-auth-url-display">{authData.auth_url}</code>
                    <button onClick={() => navigator.clipboard.writeText(authData.auth_url).then(() => toast.success('Copied'))} className="shrink-0 p-1 hover:bg-gray-200 rounded" data-testid="gmail-auth-url-copy"><Copy size={13} /></button>
                    <button onClick={() => window.open(authData.auth_url, '_blank', 'noopener,noreferrer')} className="shrink-0 p-1 hover:bg-gray-200 rounded" data-testid="gmail-auth-url-open"><ExternalLink size={13} /></button>
                  </div>
                </li>
                <li>
                  <p className="text-xs text-gray-600 mb-1">3. Paste the returned URL or just the code</p>
                  <div className="flex gap-2">
                    <input
                      value={authCode} onChange={(e) => setAuthCode(e.target.value)}
                      placeholder="Paste full redirect URL or just the code value"
                      className="flex-1 px-3 py-2 text-xs font-mono border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                      data-testid="gmail-auth-code-input"
                    />
                    <button onClick={handleSubmitCode} disabled={connecting || !authCode.trim()} className="px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-1.5" data-testid="gmail-submit-code-btn">
                      {connecting ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                      Connect Gmail
                    </button>
                  </div>
                </li>
              </>
            )}
          </ol>
        </Section>
      )}

      {/* CARD 5 — Tools */}
      <Section icon={ShieldCheck} title="Tools" subtitle="Validate, test, send" testid="gmail-card-tools">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button onClick={handleValidate} disabled={validating} className="px-3 py-1.5 border border-gray-200 text-gray-700 text-xs rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5" data-testid="gmail-validate-btn">
            {validating ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
            Validate Credentials
          </button>
          <button onClick={handleTestConnection} disabled={testingConn} className="px-3 py-1.5 border border-gray-200 text-gray-700 text-xs rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5" data-testid="gmail-test-conn-btn">
            {testingConn ? <Loader2 size={12} className="animate-spin" /> : <FileJson size={12} />}
            Test Connection
          </button>
        </div>

        {connTest && (
          <div className={`mb-3 px-3 py-2 rounded-lg border text-xs ${connTest.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`} data-testid="gmail-test-conn-result">
            {connTest.ok ? <CheckCircle size={12} className="inline mr-1" /> : <AlertTriangle size={12} className="inline mr-1" />}
            <strong>Stage:</strong> {connTest.stage}
            {connTest.detail && <> · {connTest.detail}</>}
            {connTest.issues && connTest.issues.length > 0 && <> · {connTest.issues.join(', ')}</>}
          </div>
        )}

        <div className="flex gap-2">
          <input
            type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
            placeholder="recipient@example.com"
            className="flex-1 px-3 py-2 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
            data-testid="gmail-test-email-input"
          />
          <button onClick={handleSendTest} disabled={sending || !connected} className="px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-1.5" data-testid="gmail-send-test-btn" title={!connected ? 'Connect Gmail first' : ''}>
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Send Test Email
          </button>
        </div>
      </Section>
    </div>
  );
};

export default GmailIntegrationSettings;
